import React, { useEffect, useState } from "react";
import { Loader2, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n";
import { TeamApi, errorMessage } from "../lib/api";
import { formatMoney } from "../lib/format";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const EMPTY = {
  enabled: true,
  mode: "vat_percent",
  vat_percent: 100,
  gross_percent: 0,
  max_per_receipt: "",
  note: "",
};

/** Per-employee refund override editor (wins over company + category rules). */
export const RefundOverrideDialog = ({ member, open, onOpenChange, currency = "EUR", onSaved }) => {
  const { t } = useI18n();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const existing = member?.refund_override;
    setForm(
      existing
        ? {
            enabled: existing.enabled !== false,
            mode: existing.mode || "vat_percent",
            vat_percent: existing.vat_percent ?? 0,
            gross_percent: existing.gross_percent ?? 0,
            max_per_receipt: existing.max_per_receipt ?? "",
            note: existing.note || "",
          }
        : EMPTY
    );
  }, [open, member]);

  const preview = () => {
    const vat = 25;
    const gross = 125;
    let amount = 0;
    if (form.mode === "vat_percent" || form.mode === "both")
      amount += (vat * (Number(form.vat_percent) || 0)) / 100;
    if (form.mode === "gross_percent" || form.mode === "both")
      amount += (gross * (Number(form.gross_percent) || 0)) / 100;
    const cap = form.max_per_receipt;
    if (cap !== "" && cap !== null && amount > Number(cap)) amount = Number(cap);
    return amount;
  };

  const save = async () => {
    setBusy(true);
    try {
      const res = await TeamApi.setOverride(member.id, {
        enabled: !!form.enabled,
        mode: form.mode,
        vat_percent: Number(form.vat_percent) || 0,
        gross_percent: Number(form.gross_percent) || 0,
        max_per_receipt:
          form.max_per_receipt === "" || form.max_per_receipt === null
            ? null
            : Number(form.max_per_receipt),
        note: form.note || null,
      });
      toast.success(t("override.saved", { count: res.receipts_recomputed }));
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(errorMessage(e, "Could not save the rule"));
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await TeamApi.clearOverride(member.id);
      toast.success(t("override.removed"));
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(errorMessage(e, "Could not remove the rule"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="refund-override-dialog">
        <DialogHeader>
          <DialogTitle>{t("override.title", { name: member?.name || "" })}</DialogTitle>
          <DialogDescription>{t("override.sub")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border p-3">
            <span className="text-sm font-medium">{t("override.enable")}</span>
            <Switch
              checked={!!form.enabled}
              onCheckedChange={(v) => setForm((p) => ({ ...p, enabled: v }))}
              data-testid="override-enable-switch"
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs">{t("override.basis")}</Label>
              <Select value={form.mode} onValueChange={(v) => setForm((p) => ({ ...p, mode: v }))}>
                <SelectTrigger data-testid="override-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vat_percent">{t("settings.modeVat")}</SelectItem>
                  <SelectItem value="gross_percent">{t("settings.modeGross")}</SelectItem>
                  <SelectItem value="both">{t("settings.modeBoth")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t("override.vatPct")}</Label>
              <Input
                className="ll-num"
                inputMode="decimal"
                disabled={form.mode === "gross_percent"}
                value={form.vat_percent}
                onChange={(e) => setForm((p) => ({ ...p, vat_percent: e.target.value }))}
                data-testid="override-vat-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t("override.grossPct")}</Label>
              <Input
                className="ll-num"
                inputMode="decimal"
                disabled={form.mode === "vat_percent"}
                value={form.gross_percent}
                onChange={(e) => setForm((p) => ({ ...p, gross_percent: e.target.value }))}
                data-testid="override-gross-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">
                {t("override.cap")} ({currency})
              </Label>
              <Input
                className="ll-num"
                inputMode="decimal"
                placeholder={t("settings.noCap")}
                value={form.max_per_receipt ?? ""}
                onChange={(e) => setForm((p) => ({ ...p, max_per_receipt: e.target.value }))}
                data-testid="override-cap-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{t("override.note")}</Label>
              <Input
                value={form.note || ""}
                onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))}
                data-testid="override-note-input"
              />
            </div>
          </div>

          <p className="rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground">
            {t("override.example", {
              gross: formatMoney(125, currency),
              vat: formatMoney(25, currency),
            })}{" "}
            <span className="ll-num font-semibold text-primary">
              {formatMoney(preview(), currency)}
            </span>
          </p>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          {member?.refund_override ? (
            <Button
              variant="ghost"
              className="text-destructive hover:text-destructive"
              onClick={clear}
              disabled={busy}
              data-testid="override-remove-button"
            >
              <Trash2 className="mr-2 h-4 w-4" /> {t("override.remove")}
            </Button>
          ) : (
            <span />
          )}
          <Button onClick={save} disabled={busy} data-testid="override-save-button">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            {t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RefundOverrideDialog;
